import type { ReactNode } from "react";

type WorkspaceProps = {
  id: string;
  children: ReactNode;
  className?: string;
  /**
   * `split` — form + companion preview rail (content composers).
   * `solo` — centered full-canvas editor when no companion panel exists.
   */
  layout?: "split" | "solo";
};

/** Full-width composer band — visually separated from inventory/inspect. */
export function ComposerWorkspace({
  id,
  children,
  className = "",
  layout = "split",
}: WorkspaceProps) {
  const solo = layout === "solo";
  return (
    <div
      id={id}
      className={[
        "composer-workspace",
        "announcements-layout",
        solo ? "composer-workspace--solo" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      data-vt-composer
      {...(solo ? { "data-vt-composer-solo": "true" } : {})}
    >
      {children}
    </div>
  );
}

type HeaderProps = {
  kind: string;
  title: string;
  description?: string;
  statusLabel?: string;
  statusTone?: "neutral" | "info" | "warning" | "success" | "danger";
  meta?: ReactNode;
  actions?: ReactNode;
};

const STATUS_BADGE: Record<NonNullable<HeaderProps["statusTone"]>, string> = {
  neutral: "state-badge--archived",
  info: "state-badge--scheduled",
  warning: "state-badge--paused",
  success: "state-badge--published",
  danger: "state-badge--emergency",
};

/** Clear editor header: type, status, safe context, optional actions. */
export function ComposerHeader({
  kind,
  title,
  description,
  statusLabel,
  statusTone = "neutral",
  meta,
  actions,
}: HeaderProps) {
  return (
    <header className="composer-header" data-vt-composer-header>
      <div className="composer-header__main">
        <p className="composer-header__kind">{kind}</p>
        <div className="composer-header__title-row">
          <h2 className="composer-header__title">{title}</h2>
          {statusLabel && (
            <span className={`state-badge ${STATUS_BADGE[statusTone]}`}>{statusLabel}</span>
          )}
        </div>
        {description && (
          <p
            className="composer-header__description"
            {...(meta ? {} : { "data-vt-composer-context": "true" })}
          >
            {description}
          </p>
        )}
        {meta && (
          <p className="composer-header__meta" data-vt-composer-context>
            {meta}
          </p>
        )}
      </div>
      {actions && <div className="composer-header__actions">{actions}</div>}
    </header>
  );
}

type SectionProps = {
  title: string;
  description?: string;
  children: ReactNode;
  id?: string;
  /** Hairline group without nested card chrome */
  bare?: boolean;
};

/** Structured field group inside a composer form. */
export function ComposerSection({ title, description, children, id, bare = false }: SectionProps) {
  const headingId = id;
  return (
    <section
      className={`composer-section${bare ? " composer-section--bare" : ""}`}
      data-vt-composer-section
      {...(headingId ? { "aria-labelledby": headingId } : {})}
    >
      <header className="composer-section__header">
        <h3 {...(headingId ? { id: headingId } : {})} className="composer-section__title">
          {title}
        </h3>
        {description && <p className="composer-section__description">{description}</p>}
      </header>
      <div className="composer-section__body">{children}</div>
    </section>
  );
}

type PanelProps = {
  children: ReactNode;
  className?: string;
};

/** Form column surface inside ComposerWorkspace (Card without legacy title chrome). */
export function ComposerPanel({ children, className = "" }: PanelProps) {
  return (
    <section className={["card", "composer-panel", className].filter(Boolean).join(" ")} data-vt-composer-panel>
      <div className="card__body composer-panel__body">{children}</div>
    </section>
  );
}
